import { useEffect, useState } from 'react';
import useSWR from 'swr';
import { useParams } from 'react-router-dom';

import { invariant } from '@utils/invariant';
import { OrderRouteMap } from 'components/map/OrderRouteMap';
import { getOrderRouteData, calculateOrderRoute } from '../../models';
import { SpinnerBlock } from 'components/Spinner';
import { formatDate, formatDateTime } from '@utils/date';
import { OrderTrackingEvents } from 'src/app/carrier-portal/orders/components/OrderTrackingEvents';
import { useTranslation } from 'src/contexts/translation-context';
import type { OrderTrackingEvent } from '../../endpoints/OrderTrackingEventsEndpoint.types';
import { useOrderMapLocation } from '../../hooks/useOrderMapLocation';
import { ErrorState } from 'components/error-state/ErrorState';

export const OrderTrackingPage = () => {
  const { orderId } = useParams<{ orderId: string }>();
  invariant(orderId);

  const { i18n } = useTranslation();
  const [isCalculating, setIsCalculating] = useState(false);
  const [hasAttemptedCalculation, setHasAttemptedCalculation] = useState(false);

  const {
    data: routeData,
    error: routeError,
    isLoading: isRouteLoading,
    mutate: mutateRouteData,
  } = useSWR(`order-route-data-${orderId}`, () => getOrderRouteData(orderId));
  const {
    trackingEvents,
    driverLocation,
    driverHeading,
    telemetryLastSeenAt,
    trailerLocation,
    trailerHeading,
    trailerPlate,
    trailerLastSeenAt,
  } = useOrderMapLocation(orderId);

  // Trigger route calculation when polyline is missing (only once per page load)
  useEffect(() => {
    if (
      !isRouteLoading &&
      routeData &&
      (!routeData.polyline || routeData.polyline.length === 0) &&
      !isCalculating &&
      !hasAttemptedCalculation
    ) {
      setIsCalculating(true);
      setHasAttemptedCalculation(true);
      calculateOrderRoute(orderId)
        .then((result) => {
          if (result.linesCalculated > 0) {
            // Refetch route data after calculation
            mutateRouteData();
          }
        })
        .catch((error) => {
          console.error('Failed to calculate route:', error);
        })
        .finally(() => {
          setIsCalculating(false);
        });
    }
  }, [orderId, routeData, isRouteLoading, isCalculating, hasAttemptedCalculation, mutateRouteData]);

  if (isRouteLoading || isCalculating) {
    return <SpinnerBlock message={isCalculating ? 'Route berekenen...' : 'Route laden...'} />;
  }

  if (routeError) {
    return <ErrorState error={routeError} onRetry={() => mutateRouteData()} variant="section" />;
  }

  if (!routeData || routeData.stops.length === 0) {
    return (
      <div className="p-4 text-fg-muted">
        <p>Geen route data beschikbaar voor deze order.</p>
      </div>
    );
  }

  const stopsWithCoordinates = routeData.stops.filter((s) => s.coordinates !== null);

  // Find tracking event for a stop (StatusLoaded or StatusUnloaded)
  const getTrackingEventForStop = (stopType: 'Load' | 'Unload', stopName: string): OrderTrackingEvent | undefined => {
    if (!trackingEvents) return undefined;
    const eventType = stopType === 'Load' ? 'StatusLoaded' : 'StatusUnloaded';
    return trackingEvents.find((e) => e.eventType === eventType && e.stopName === stopName);
  };

  if (stopsWithCoordinates.length === 0) {
    return (
      <div className="p-4 text-fg-muted">
        <p>Geen locatie coördinaten beschikbaar voor de stops.</p>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4">
      <div className="h-[500px]">
        <OrderRouteMap
          routeCoordinates={routeData.polyline ?? []}
          stops={stopsWithCoordinates.map((s) => ({
            type: s.type,
            name: s.name,
            coordinates: s.coordinates as [number, number],
          }))}
          driverLocation={driverLocation}
          driverHeading={driverHeading}
          trailerLocation={trailerLocation}
          trailerHeading={trailerHeading}
          trailerLabel={trailerPlate}
          className="h-full w-full rounded-lg overflow-hidden"
        />
      </div>
      {/* Which source the marker came from, when it is not the driver app: a telemetry position is only
          as fresh as this page load, so it says when the truck last reported it. */}
      {telemetryLastSeenAt && (
        <p className="text-sm text-fg-muted">
          {i18n('liveTracking.lastSignal')}: {formatDateTime(telemetryLastSeenAt)}
        </p>
      )}
      {/* The trailer's own line, so the two markers are told apart: the plate names it, and its timestamp
          says how old the position under its marker is. Nothing is drawn for a viewer who may not see it. */}
      {trailerLastSeenAt && trailerPlate && (
        <p className="text-sm text-fg-muted">
          {i18n('liveTracking.trailer')}: {trailerPlate} · {i18n('liveTracking.lastSignal')}:{' '}
          {formatDateTime(trailerLastSeenAt)}
        </p>
      )}

      {/* Stops list and Tracking Events side by side on larger screens */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Stops list */}
        <div>
          <h3 className="font-medium mb-2">Stops</h3>
          <div className="space-y-2">
            {routeData.stops.map((stop, index) => {
              const trackingEvent = getTrackingEventForStop(stop.type, stop.name);
              return (
                <div key={index} className="flex items-center gap-3 p-3 bg-surface-sunken rounded-lg">
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-fg-inverse text-sm font-medium ${
                      stop.type === 'Load' ? 'bg-positive' : 'bg-info'
                    }`}
                  >
                    {index + 1}
                  </div>
                  <div className="flex-1">
                    <div className="font-medium">{stop.name}</div>
                    <div className="text-sm text-fg-muted">{stop.type === 'Load' ? 'Laden' : 'Lossen'}</div>
                    {trackingEvent ? (
                      <div className="text-sm text-positive-text">{formatDateTime(trackingEvent.createdAt)}</div>
                    ) : (
                      stop.date && (
                        <div className="text-sm text-fg-muted">
                          {i18n('carrierPortal.tracking.scheduled')}: {formatDate(stop.date)}
                        </div>
                      )
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Tracking Events */}
        <div>
          <h3 className="font-medium mb-2">{i18n('carrierPortal.tracking.events')}</h3>
          <OrderTrackingEvents orderId={orderId} />
        </div>
      </div>
    </div>
  );
};
